import type { CannedScenario, Confidence } from "../data/types";

interface Props {
  scenario: CannedScenario;
  onContinue: () => void;
  onEscalate: () => void;
}

export function TriageCard({ scenario, onContinue, onEscalate }: Props) {
  const sev = scenario.triage_card.severity.toLowerCase();

  return (
    <div className="triage-card card">
      <div className="tc-head">
        <span className={`badge severity-${sev} lg`}>{scenario.triage_card.severity}</span>
        <div className="tc-ident">
          <div className="id mono">{scenario.incident.incident_id}</div>
          <div className="who">{scenario.triage_card.incident_type.replace(/_/g, " ")}</div>
        </div>
        <div className="tc-timing">
          <span className={`badge confidence-${scenario.triage_card.confidence}`}>
            confidence: {scenario.triage_card.confidence}
          </span>
          <div className="tc-agreement muted">
            {scenario.triage_card.sub_agent_agreement} agreement
          </div>
        </div>
      </div>

      <div className="tc-section">
        <div className="tc-label">Summary</div>
        <div className="tc-summary">{scenario.triage_card.summary}</div>
      </div>

      <div className="tc-section">
        <div className="tc-label">Sub-agent breakdown</div>
        <div className="confidence-breakdown">
          <ConfCell
            name="Command"
            conf={scenario.command_findings?.confidence}
            note={
              scenario.command_findings
                ? scenario.command_findings.intent.replace(/_/g, " ")
                : "skipped — no command_line in incident"
            }
          />
          <ConfCell
            name="Identity"
            conf={scenario.identity_findings?.confidence}
            note={
              scenario.identity_findings
                ? `${scenario.identity_findings.compromise_likelihood} likelihood`
                : "skipped — no user dimension"
            }
          />
          <ConfCell
            name="Network"
            conf={scenario.network_findings?.confidence}
            note={
              scenario.network_findings
                ? scenario.network_findings.destination_reputation.replace(/_/g, " ")
                : "skipped — no network indicator"
            }
          />
          <ConfCell
            name="Activity"
            conf={scenario.activity_findings?.confidence}
            note={
              scenario.activity_findings
                ? `${scenario.activity_findings.baseline_deviations.length} baseline deviations`
                : "skipped"
            }
          />
        </div>
      </div>

      <div className="tc-section">
        <div className="tc-label">Indicators</div>
        <div className="indicators-grid">
          {scenario.triage_card.indicators.users.length > 0 && (
            <Indicator label="users" values={scenario.triage_card.indicators.users} />
          )}
          {scenario.triage_card.indicators.hostnames.length > 0 && (
            <Indicator label="hostnames" values={scenario.triage_card.indicators.hostnames} />
          )}
          {scenario.triage_card.indicators.ips.length > 0 && (
            <Indicator label="ips" values={scenario.triage_card.indicators.ips} />
          )}
          {scenario.triage_card.indicators.domains.length > 0 && (
            <Indicator label="domains" values={scenario.triage_card.indicators.domains} />
          )}
        </div>
      </div>

      <div className="tc-actions">
        <button type="button" className="button ghost" onClick={onEscalate}>
          Escalate to L2
        </button>
        <button type="button" className="button primary" onClick={onContinue}>
          Generate action plan →
        </button>
      </div>
    </div>
  );
}

function ConfCell({
  name,
  conf,
  note,
}: {
  name: string;
  conf: Confidence | undefined;
  note: string;
}) {
  const skipped = !conf;
  return (
    <div className={`conf-cell ${skipped ? "skipped" : ""}`}>
      <div className="conf-name">{name}</div>
      {!skipped ? (
        <div className="conf-bar">
          <span className={`badge confidence-${conf}`}>{conf}</span>
        </div>
      ) : (
        <div className="conf-bar">
          <span className="badge skipped">skipped</span>
        </div>
      )}
      <div className="conf-note">{note}</div>
    </div>
  );
}

function Indicator({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="indicator">
      <div className="indicator-label">{label}</div>
      <div className="indicator-values mono">{values.join(", ")}</div>
    </div>
  );
}
